/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { ChangeEvent, useEffect } from 'react';
import { useForm, UseFormGetValues } from 'react-hook-form';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  FormHelperText,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { useFetchUserName } from '../../hooks/useFetchUserName';
import {
  Assessment,
  Criteria,
  Idea,
  JuryAssessmentFormValues,
} from '../../types';
import { JURY_ASSESSMENT_SCORE_OPTIONS } from '../../util/dropDownOptions';
import { InputField } from '../InputField/InputField';
import { InputSelector } from '../InputSelector';

const useStyles = makeStyles(theme => ({
  description: {
    maxHeight: '400px',
    overflow: 'auto',
  },
  htmlBlock: {
    '& a': {
      color: theme.palette.primary.main,
    },
  },
}));

type Props = {
  handleSave: (
    getValues: UseFormGetValues<JuryAssessmentFormValues>,
  ) => Promise<void>;
  idea: Idea | null | undefined;
  selfAssessment: Assessment | null | undefined;
  defaultValues?: JuryAssessmentFormValues;
  deleteButton?: React.JSX.Element;
  handleClose: () => void;
  displaySummary?: boolean;
};

export const Form = ({
  handleSave,
  idea,
  selfAssessment,
  defaultValues,
  deleteButton,
  displaySummary,
}: Props) => {
  const { handleSubmit, reset, control, getValues } =
    useForm<JuryAssessmentFormValues>({
      mode: 'onChange',
      defaultValues,
    });

  const [expanded, setExpanded] = React.useState('information');
  const { value: responsibleName } = useFetchUserName(idea?.responsible);
  const classes = useStyles();

  const techFeasibilityScore = selfAssessment?.scores.find(
    score => score.criteria === ('Technical Feasibility' as Criteria),
  );
  const strategicFitScore = selfAssessment?.scores.find(
    score => score.criteria === ('Strategic Fit' as Criteria),
  );
  const financialPotentialScore = selfAssessment?.scores.find(
    score => score.criteria === ('Financial Potential' as Criteria),
  );
  const innovativenessScore = selfAssessment?.scores.find(
    score => score.criteria === ('Innovativeness' as Criteria),
  );

  const handleChange =
    (panel: string) => (_event: ChangeEvent<{}>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : '');
    };

  useEffect(() => {
    if (defaultValues) {
      reset({ ...defaultValues });
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    setExpanded(defaultValues === undefined ? 'information' : 'assessment');
  }, [defaultValues, setExpanded]);

  const handleSaveForm = () => {
    handleSave(getValues);
  };

  const JuryAssessmentInputField = InputField<JuryAssessmentFormValues>;
  const JuryAssessmentInputSelector = InputSelector<JuryAssessmentFormValues>;

  return (
    <div>
      {displaySummary && (
        <Accordion
          expanded={expanded === 'information'}
          onChange={handleChange('information')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h4" component="h2">
              Idea information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Card>
              <CardContent>
                <Grid container spacing={5}>
                  <Grid item xs={9}>
                    <Typography variant="body1" paragraph>
                      {idea?.summary}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="h5">Description</Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      <div
                        className={classes.htmlBlock}
                        dangerouslySetInnerHTML={{
                          __html: idea?.description!,
                        }}
                      />
                    </Typography>
                  </Grid>
                  <Grid
                    style={{
                      justifyContent: 'flex-end',
                    }}
                    item
                    xs={8}
                  >
                    <>
                      <Typography variant="h5">Responsible</Typography>
                      <Typography variant="body2">
                        {responsibleName || ''}
                      </Typography>
                    </>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="h5">Solution</Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      <div
                        className={classes.htmlBlock}
                        dangerouslySetInnerHTML={{
                          __html: idea?.solution!,
                        }}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h5">Self Assessment</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body2">
                      Technical Feasibility
                    </Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {techFeasibilityScore?.description}
                    </Typography>
                  </Grid>
                  <Grid
                    style={{
                      justifyContent: 'flex-end',
                    }}
                    item
                    xs={3}
                  >
                    <Typography variant="body2">
                      Technical Feasibility Score
                    </Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {techFeasibilityScore?.score}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body2">Strategic Fit</Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {strategicFitScore?.description}
                    </Typography>
                  </Grid>
                  <Grid
                    style={{
                      justifyContent: 'flex-end',
                    }}
                    item
                    xs={3}
                  >
                    <Typography variant="body2">Strategic Fit Score</Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {strategicFitScore?.score}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body2">Impact</Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {financialPotentialScore?.description}
                    </Typography>
                  </Grid>
                  <Grid
                    style={{
                      justifyContent: 'flex-end',
                    }}
                    item
                    xs={3}
                  >
                    <Typography variant="body2">Impact Score</Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {financialPotentialScore?.score}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body2">Innovativeness</Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {innovativenessScore?.description}
                    </Typography>
                  </Grid>
                  <Grid
                    style={{
                      justifyContent: 'flex-end',
                    }}
                    item
                    xs={3}
                  >
                    <Typography variant="body2">
                      Innovativeness Score
                    </Typography>
                    <Typography
                      className={classes.description}
                      variant="body1"
                      component="div"
                    >
                      {techFeasibilityScore?.score}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion
        expanded={expanded === 'assessment'}
        onChange={handleChange('assessment')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography variant={displaySummary ? 'h4' : 'h5'} component="h2">
            {displaySummary ? 'Assessment' : 'Jury Assessment'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            display="flex"
            flexDirection="column"
            gridRowGap="20px"
            flexGrow={1}
          >
            <Box display="flex" flexDirection="column" gridRowGap="20px">
              <Typography variant="h6">Technical Feasibility</Typography>
              <Grid container>
                <Grid item xs={10}>
                  <JuryAssessmentInputField
                    control={control}
                    rules={{
                      required: false,
                    }}
                    inputType="techFeasibility"
                    label="Please provide your assessment on the Technical feasibility of this idea"
                  />
                </Grid>
                <Grid item xs={2}>
                  <JuryAssessmentInputSelector
                    control={control}
                    name="techFeasibilityScore"
                    options={JURY_ASSESSMENT_SCORE_OPTIONS}
                    label="Technical Feasibility Score"
                    isRequired={false}
                  />
                  <FormHelperText>
                    <Typography variant="caption" color="secondary">
                      1 = low &emsp; &emsp; 5 = high
                    </Typography>
                  </FormHelperText>
                </Grid>
              </Grid>
            </Box>
            <Box display="flex" flexDirection="column" gridRowGap="20px">
              <Typography variant="h6">Strategic Fit</Typography>
              <Grid container>
                <Grid item xs={10}>
                  <JuryAssessmentInputField
                    control={control}
                    rules={{
                      required: false,
                    }}
                    inputType="strategicFit"
                    label="Please provide your assessment on the Strategic Fit of this idea"
                  />
                </Grid>
                <Grid item xs={2}>
                  <JuryAssessmentInputSelector
                    control={control}
                    name="strategicFitScore"
                    options={JURY_ASSESSMENT_SCORE_OPTIONS}
                    label="Strategic Fit Score"
                    isRequired={false}
                  />
                  <FormHelperText>
                    <Typography variant="caption" color="secondary">
                      1 = low &emsp; &emsp; 5 = high
                    </Typography>
                  </FormHelperText>
                </Grid>
              </Grid>
            </Box>
            <Box display="flex" flexDirection="column" gridRowGap="20px">
              <Typography variant="h6">Impact</Typography>
              <Grid container>
                <Grid item xs={10}>
                  <JuryAssessmentInputField
                    control={control}
                    rules={{
                      required: false,
                    }}
                    inputType="financialPotential"
                    label="Please provide your assessment on the potential Impact of this idea"
                  />
                </Grid>
                <Grid item xs={2}>
                  <JuryAssessmentInputSelector
                    control={control}
                    name="financialPotentialScore"
                    options={JURY_ASSESSMENT_SCORE_OPTIONS}
                    label="Impact Score"
                    isRequired={false}
                  />
                  <FormHelperText>
                    <Typography variant="caption" color="secondary">
                      1 = low &emsp; &emsp; 5 = high
                    </Typography>
                  </FormHelperText>
                </Grid>
              </Grid>
            </Box>
            <Box display="flex" flexDirection="column" gridRowGap="20px">
              <Typography variant="h6">Innovativeness</Typography>
              <Grid container>
                <Grid item xs={10}>
                  <JuryAssessmentInputField
                    control={control}
                    rules={{
                      required: false,
                    }}
                    inputType="innovativeness"
                    label="Please provide your assessment on the Innovativeness of this idea"
                  />
                </Grid>
                <Grid item xs={2}>
                  <JuryAssessmentInputSelector
                    control={control}
                    name="innovativenessScore"
                    options={JURY_ASSESSMENT_SCORE_OPTIONS}
                    label="Innovativeness Score"
                    isRequired={false}
                  />
                  <FormHelperText>
                    <Typography variant="caption" color="secondary">
                      1 = low &emsp; &emsp; 5 = high
                    </Typography>
                  </FormHelperText>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      <br />
      <Box display="flex" flexDirection="column" gridRowGap="30px">
        <Box>
          {deleteButton}
          <Button
            onClick={handleSubmit(handleSaveForm)}
            color="primary"
            variant="outlined"
            type="submit"
          >
            {defaultValues !== undefined
              ? 'Update Assessment'
              : 'Save Assessment'}
          </Button>
        </Box>
      </Box>
    </div>
  );
};
